import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  ModalProps,
  notification,
} from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { omit } from 'lodash-es'
import { FunctionComponent, useEffect } from 'react'
import styled from 'styled-components'
import { nameof } from 'ts-simple-nameof'
import { useEditDirectoryKolBasicInfoMutation } from '@/api/workspace-api/directory-api'
import CustomizedLabelsSelect from '@/components/common/selectors/customized-labels-select'
import { useKolContactInfo } from '@/components/kol/common/kol-cooperation-detail-drawer/contact-info/use-kol-contact-info'
import { DEFAULT_DATE_FORMAT } from '@/constants/date-format'
import useDirectoryLimit from '@/hooks/use-directory-limit'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  BasicInfo,
  type CustomizedLabel,
} from '@/types/schema/directory/kol-management'
import logger from '@/utils/logger'

interface BasicInfoFormData
  extends Omit<BasicInfo, 'customized_labels' | 'birthday'> {
  customized_labels?: CustomizedLabel[]
  birthday: Dayjs | null
}

interface BasicInfoModalProps extends Pick<ModalProps, 'open'> {
  kolId: string
  data: BasicInfo
  onClose: () => void
  isCreate: boolean
}

const BasicInfoModal: FunctionComponent<BasicInfoModalProps> = ({
  open,
  onClose,
  data,
  kolId,
  isCreate,
}) => {
  const { formatMessage } = useIntl()
  const [form] = Form.useForm<BasicInfoFormData>()

  const { kolLabelAmount } = useDirectoryLimit()
  const [updateBasicInfo, { isLoading: isUpdatingBasicInfo }] =
    useEditDirectoryKolBasicInfoMutation()
  const { handleEditBasicInfoEvent } = useKolContactInfo(kolId)

  useEffect(() => {
    form.setFieldsValue({
      ...data,
      birthday: data.birthday === '' ? null : dayjs(data.birthday),
    })
  }, [data, form])

  const handleSubmit = async (values: BasicInfoFormData): Promise<void> => {
    const birthday = values.birthday?.toISOString() ?? ''
    const customized_label_ids =
      values.customized_labels?.map((label) => label.id) ?? []

    try {
      await updateBasicInfo({
        kolId,
        basicInfo: {
          ...omit(values, 'customized_labels'),
          birthday,
          customized_label_ids,
        },
      }).unwrap()

      handleEditBasicInfoEvent({
        ...values,
        birthday,
        customized_labels: values.customized_labels ?? [],
      })

      onClose()
    } catch (error) {
      logger.error(error)
      notification.error({
        message: formatMessage({ id: 'general:operation_failure' }),
        placement: 'bottomRight',
      })
    }
  }

  return (
    <Modal
      centered
      destroyOnClose
      footer={
        <Button
          htmlType='submit'
          loading={isUpdatingBasicInfo}
          type='primary'
          onClick={form.submit}
        >
          {formatMessage({ id: 'general:submit_btn' })}
        </Button>
      }
      open={open}
      title={
        <ModalTitle>
          {formatMessage({
            id: isCreate
              ? 'directory:contact_basic_info_modal_title_create'
              : 'directory:contact_basic_info_modal_title_edit',
          })}
        </ModalTitle>
      }
      onCancel={onClose}
    >
      <Form form={form} layout='vertical' onFinish={handleSubmit}>
        <Form.Item
          label={formatMessage({ id: 'kol:field_birthday' })}
          name={nameof<BasicInfoFormData>((n) => n.birthday)}
        >
          <DatePicker
            format={DEFAULT_DATE_FORMAT}
            placeholder={formatMessage({
              id: 'directory:contact_fields_placeholder_birthday',
            })}
            style={{ width: '100%' }}
            suffixIcon={null}
          />
        </Form.Item>
        <Form.Item
          label={formatMessage({
            id: 'directory:search_field_type_profession',
          })}
          name={nameof<BasicInfoFormData>((n) => n.profession)}
        >
          <Input
            placeholder={formatMessage({
              id: 'directory:contact_fields_placeholder_profession',
            })}
          />
        </Form.Item>
        <Form.Item
          label={formatMessage({ id: 'general:field_location' })}
          name={nameof<BasicInfoFormData>((n) => n.address)}
        >
          <Input
            placeholder={formatMessage({
              id: 'directory:contact_fields_placeholder_address',
            })}
          />
        </Form.Item>
        <Form.Item
          label={formatMessage({
            id: 'directory:search_field_type_tags',
          })}
          name={nameof<BasicInfoFormData>((n) => n.customized_labels)}
        >
          <CustomizedLabelsSelect editable maxCreationCount={kolLabelAmount} />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default BasicInfoModal

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }): string => theme.colors.text.primary};
  margin-bottom: 24px;
`
